export type TabPitchClass =
  | 'C'
  | 'C#'
  | 'D'
  | 'D#'
  | 'E'
  | 'F'
  | 'F#'
  | 'G'
  | 'G#'
  | 'A'
  | 'A#'
  | 'B';
export interface GuitarTuning {
  name: string;
  notes: TabPitchClass[];
}

/** Tab string numbers: 1 = high E, 6 = low E. Stage indices use 0 = low E. */
export type TabString = 1 | 2 | 3 | 4 | 5 | 6;
export interface TabNote {
  string: TabString;
  fret: number;
}
export type TabTechnique =
  | {
      type: 'slide' | 'hammerOn' | 'pullOff';
      string: TabString;
      fromFret: number;
      toFret: number;
    }
  | { type: 'bend'; string: TabString; fromFret: number; semitones?: number }
  | { type: 'vibrato' | 'release'; string: TabString; fromFret: number };
interface EventPosition {
  id: string;
  measure: number;
  /** Quarter-note beats throughout, including compound time. 12/8 = 6 beats. */
  startBeat: number;
  durationBeats: number;
  source?: { page: number; review?: string };
}
export type TabEvent = EventPosition &
  (
    | {
        type: 'note';
        notes: TabNote[];
        attack: 'pick' | 'hold' | 'legato';
        technique?: TabTechnique;
      }
    | { type: 'rest'; notes: []; technique?: never }
  );
export interface TabMeasure {
  number: number;
  timeSignature: { numerator: number; denominator: number };
  startBeat: number;
  durationBeats: number;
}
export interface TabSection {
  id: string;
  name: string;
  startMeasure: number;
  endMeasure: number;
}
export interface TabScale {
  root: TabPitchClass;
  name:
    | 'Major (Ionian)'
    | 'Minor (Aeolian)'
    | 'Major Pentatonic'
    | 'Minor Pentatonic';
  basis: 'source' | 'inferred' | 'researched';
}
export interface TabChord {
  label: string;
  root: TabPitchClass;
  quality: 'major' | 'minor' | 'dominant';
  startBeat: number;
  durationBeats: number;
  basis: 'source' | 'inferred' | 'researched';
}
export interface TabSong {
  id: string;
  title: string;
  artist?: string;
  /** Reuses JamCam's low-to-high tuning convention. Event string numbers are converted at the stage boundary. */
  tuning: GuitarTuning;
  defaultBpm: number;
  sections: TabSection[];
  /** Optional enrichment; older private tabs remain playable. */
  scale?: TabScale;
  chords?: TabChord[];
  measures: TabMeasure[];
  events: TabEvent[];
  source: { title: string; description: string; reviewNotes: string[] };
}
export const toStageString = (string: TabString) => 6 - string;
export const TAB_DEFAULT_PRACTICE_BPM = 70;

const validPosition = (s: unknown, f: unknown) =>
  Number.isInteger(s) &&
  Number(s) >= 1 &&
  Number(s) <= 6 &&
  Number.isInteger(f) &&
  Number(f) >= 0 &&
  Number(f) <= 24;
const validTechnique = (t: any) =>
  !!t &&
  typeof t === 'object' &&
  !Array.isArray(t) &&
  validPosition(t.string, t.fromFret) &&
  ['slide', 'hammerOn', 'pullOff', 'bend', 'release', 'vibrato'].includes(
    t.type
  ) &&
  (!['slide', 'hammerOn', 'pullOff'].includes(t.type) ||
    validPosition(t.string, t.toFret)) &&
  (t.semitones === undefined ||
    (typeof t.semitones === 'number' &&
      Number.isFinite(t.semitones) &&
      t.semitones > 0 &&
      t.semitones <= 4));

/** Import-only recovery. Musical positions/timing must still pass validateTabSong. */
export function simplifyImportedTabSong(input: unknown): unknown {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return input;
  const song = input as TabSong;
  if (!Array.isArray(song.events)) return input;
  const aliases: Record<string, TabTechnique['type']> = {
    'hammer-on': 'hammerOn',
    hammer_on: 'hammerOn',
    hammeron: 'hammerOn',
    'pull-off': 'pullOff',
    pull_off: 'pullOff',
    pulloff: 'pullOff',
  };
  let simplified = 0;
  const events = song.events.map((event) => {
    if (!event || event.technique === undefined) return event;
    const original = event.technique as any;
    const alias =
      original && Object.prototype.hasOwnProperty.call(aliases, original.type)
        ? aliases[original.type]
        : undefined;
    const technique = alias ? { ...original, type: alias } : original;
    if (event.type === 'note' && validTechnique(technique))
      return { ...event, technique };
    simplified++;
    const { technique: ignored, ...plain } = event;
    return {
      ...plain,
      ...(event.type === 'note' && event.attack === 'legato'
        ? { attack: 'pick' }
        : {}),
    };
  });
  const review = simplified
    ? [
        `Simplified unsupported or incomplete techniques in ${simplified} events. Play those notes normally; fret positions and timing are unchanged.`,
      ]
    : [];
  return {
    ...song,
    events,
    ...(song.source && Array.isArray(song.source.reviewNotes)
      ? {
          source: {
            ...song.source,
            reviewNotes: [...song.source.reviewNotes, ...review],
          },
        }
      : {}),
  };
}

/** Runtime boundary shared by API, imported drafts and the player. Never trusts model output. */
export function validateTabSong(input: unknown): string[] {
  const errors: string[] = [];
  const record = (x: unknown): x is Record<string, any> =>
    !!x && typeof x === 'object' && !Array.isArray(x);
  const text = (x: unknown, max = 160) =>
    typeof x === 'string' && x.trim().length > 0 && x.length <= max;
  const finite = (x: unknown): x is number =>
    typeof x === 'number' && Number.isFinite(x);
  const positive = (x: unknown): x is number => finite(x) && x > 0;
  const equal = (a: number, b: number) => Math.abs(a - b) < 0.000001;
  if (!record(input)) return ['Expected a tab song'];
  const song = input;
  if (
    !text(song.id) ||
    !text(song.title) ||
    (song.artist !== undefined && !text(song.artist))
  )
    errors.push('Missing or invalid song identity');
  if (!positive(song.defaultBpm) || song.defaultBpm > 300)
    errors.push('Tempo must be between 1 and 300 BPM');
  const pitches = [
    'C',
    'C#',
    'D',
    'D#',
    'E',
    'F',
    'F#',
    'G',
    'G#',
    'A',
    'A#',
    'B',
  ];
  if (
    !record(song.tuning) ||
    !text(song.tuning.name) ||
    !Array.isArray(song.tuning.notes) ||
    song.tuning.notes.length !== 6 ||
    song.tuning.notes.some((n: unknown) => !pitches.includes(String(n)))
  )
    errors.push('Expected six tuning notes, low to high');
  if (
    !record(song.source) ||
    !text(song.source.title) ||
    !text(song.source.description, 2000) ||
    !Array.isArray(song.source.reviewNotes) ||
    song.source.reviewNotes.some((n: unknown) => !text(n, 1000))
  )
    errors.push('Invalid source notes');
  if (
    !Array.isArray(song.events) ||
    !song.events.length ||
    !Array.isArray(song.measures) ||
    !song.measures.length ||
    !Array.isArray(song.sections) ||
    !song.sections.length
  )
    return [...errors, 'Expected measures, events and sections'];
  let measureEnd = 0;
  const measures = new Map<number, TabMeasure>();
  for (const m of song.measures) {
    if (
      !record(m) ||
      !Number.isInteger(m.number) ||
      m.number < 1 ||
      measures.has(m.number) ||
      !finite(m.startBeat) ||
      !equal(m.startBeat, measureEnd) ||
      !positive(m.durationBeats) ||
      !record(m.timeSignature) ||
      !Number.isInteger(m.timeSignature.numerator) ||
      m.timeSignature.numerator < 1 ||
      m.timeSignature.numerator > 32 ||
      ![1, 2, 4, 8, 16, 32].includes(m.timeSignature.denominator) ||
      !equal(
        m.durationBeats,
        (m.timeSignature.numerator * 4) / m.timeSignature.denominator
      )
    )
      return [...errors, 'Invalid or discontinuous measures'];
    measures.set(m.number, m as unknown as TabMeasure);
    measureEnd = m.startBeat + m.durationBeats;
  }
  let end = 0;
  const ids = new Set<string>();
  for (const event of song.events) {
    if (
      !record(event) ||
      !text(event.id) ||
      ids.has(event.id) ||
      !finite(event.startBeat) ||
      !equal(event.startBeat, end) ||
      !positive(event.durationBeats)
    )
      return [...errors, 'Invalid timeline or duplicate event'];
    ids.add(event.id);
    const measure = measures.get(event.measure);
    if (
      !measure ||
      event.startBeat < measure.startBeat - 0.000001 ||
      event.startBeat + event.durationBeats >
        measure.startBeat + measure.durationBeats + 0.000001
    )
      errors.push(`Invalid measure at ${event.id}`);
    if (
      !Array.isArray(event.notes) ||
      (event.type === 'rest'
        ? event.notes.length !== 0 || event.technique !== undefined
        : event.type !== 'note' ||
          !['pick', 'hold', 'legato'].includes(event.attack) ||
          event.notes.length < 1 ||
          event.notes.length > 6 ||
          event.notes.some(
            (n: unknown) => !record(n) || !validPosition(n.string, n.fret)
          ))
    )
      errors.push(`Invalid position at ${event.id}`);
    if (
      event.type === 'note' &&
      Array.isArray(event.notes) &&
      new Set(event.notes.map((n: any) => n?.string)).size !==
        event.notes.length
    )
      errors.push('Only one fret per string per event');
    if (event.technique !== undefined) {
      if (!validTechnique(event.technique))
        errors.push(`Invalid technique at ${event.id}`);
    }
    end = event.startBeat + event.durationBeats;
  }
  if (!equal(end, measureEnd))
    errors.push('Passage and measure lengths differ');
  const bases = ['source', 'inferred', 'researched'];
  if (
    song.scale !== undefined &&
    (!record(song.scale) ||
      !pitches.includes(song.scale.root) ||
      ![
        'Major (Ionian)',
        'Minor (Aeolian)',
        'Major Pentatonic',
        'Minor Pentatonic',
      ].includes(song.scale.name) ||
      !bases.includes(song.scale.basis))
  )
    errors.push('Invalid scale context');
  if (song.chords !== undefined) {
    if (!Array.isArray(song.chords)) errors.push('Invalid chord timeline');
    else {
      let chordEnd = 0;
      for (const chord of song.chords) {
        if (
          !record(chord) ||
          !text(chord.label, 80) ||
          !pitches.includes(chord.root) ||
          !['major', 'minor', 'dominant'].includes(chord.quality) ||
          !bases.includes(chord.basis) ||
          !finite(chord.startBeat) ||
          chord.startBeat < chordEnd - 0.000001 ||
          !positive(chord.durationBeats) ||
          chord.startBeat + chord.durationBeats > measureEnd + 0.000001
        ) {
          errors.push('Invalid chord timeline');
          break;
        }
        chordEnd = chord.startBeat + chord.durationBeats;
      }
    }
  }
  const sectionIds = new Set<string>();
  let sectionEnd = 0;
  for (const s of song.sections) {
    if (
      !record(s) ||
      !text(s.id) ||
      !text(s.name) ||
      sectionIds.has(s.id) ||
      !measures.has(s.startMeasure) ||
      !measures.has(s.endMeasure)
    )
      return [...errors, 'Invalid section'];
    const first = measures.get(s.startMeasure)!;
    const last = measures.get(s.endMeasure)!;
    if (!equal(first.startBeat, sectionEnd) || last.startBeat < first.startBeat)
      errors.push('Sections must cover the timeline in order without overlap');
    sectionEnd = last.startBeat + last.durationBeats;
    sectionIds.add(s.id);
  }
  if (!equal(sectionEnd, end)) errors.push('Sections must cover the passage');
  return errors;
}

export interface TabSummary {
  id: string;
  title: string;
  artist?: string;
  createdAt: string;
  bpm: number;
  measureCount: number;
  eventCount: number;
  tuning: string;
  visibility: 'private';
  schemaVersion: 1;
}
export interface SavedTab extends TabSummary {
  song: TabSong;
  configType?: 'TAB';
  lastViewedAt?: string;
}
export type TabStatus = 'pending' | 'processing' | 'success' | 'failed';
export type TabFileFormat = 'pdf' | 'png' | 'jpeg' | 'txt';

/** Lightweight library metadata. Source and playable config are fetched on demand. */
export interface TabLibraryItem {
  id: string;
  title: string;
  artist?: string;
  createdAt: string;
  lastViewedAt?: string;
  bpm?: number;
  status: TabStatus;
  /** Old saved tabs may no longer have their original source. */
  type?: 'TEXT' | 'FILE';
  sourceAvailable: boolean;
  configType: 'TAB';
  message?: string;
  errorCode?: string;
  measureCount?: number;
  eventCount?: number;
  tuning?: string;
  visibility: 'private';
  schemaVersion: 1;
}

export type TabOriginalSource =
  | { type: 'TEXT'; textContent: string }
  | {
      type: 'FILE';
      fileName?: string;
      contentType: string;
      /** Short-lived owner-authorized URLs, never stored as permanent links. */
      url: string;
      downloadUrl: string;
      expiresAt: number;
    };

export interface TabImportInput {
  title?: string;
  artist?: string;
  text?: string;
  file?: {
    format: TabFileFormat;
    fileName?: string;
    /** Legacy inline requests. New uploads use an owner-scoped private object. */
    base64?: string;
    uploadId?: string;
    size?: number;
  };
  instructions?: string;
  timing?: 'source' | 'even-eighths';
  bpm?: number;
}
export interface TabImportJob {
  id: string;
  /** queued/ready are accepted only for pre-library imports and old clients. */
  status: TabStatus | 'queued' | 'ready';
  createdAt: string;
  processingStartedAt?: string;
  expiresAt?: number;
  song?: TabSong;
  message?: string;
  errorCode?: string;
}
export const TAB_UPLOAD_MAX_BYTES = 20 * 1024 * 1024;
export const TAB_UPLOAD_MAX_MB = 20;
export interface TabUpload {
  uploadId: string;
  url: string;
  headers: Record<string, string>;
}
